import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            type: 'bot',
            text: 'Hello! 👋 Welcome to BikeZone. How can I help you today?',
            timestamp: new Date()
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [allProducts, setAllProducts] = useState([]);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Fetch products when chatbot opens
    useEffect(() => {
        if (isOpen && allProducts.length === 0) {
            axios.get('https://bikezone-server.onrender.com/products')
                .then(({ data }) => {
                    setAllProducts(data);
                })
                .catch(error => {
                    console.error('Error fetching products:', error);
                });
        }
    }, [isOpen, allProducts.length]);

    // Extract unique brands from products
    const getUniqueBrands = () => {
        const brands = allProducts.map(product => {
            const title = product.title || '';
            // Extract brand (first word usually)
            const brand = title.split(' ')[0];
            return brand;
        });
        return [...new Set(brands)].filter(Boolean);
    };

    // Search bikes by brand
    const searchByBrand = (brand) => {
        const bikes = allProducts.filter(product => 
            product.title?.toLowerCase().includes(brand.toLowerCase())
        );
        return bikes;
    };

    // Search bikes by emission/cc
    const searchByEmission = (emission) => {
        const bikes = allProducts.filter(product => 
            product.emission?.toLowerCase().includes(emission.toLowerCase()) ||
            product.displacement?.toLowerCase().includes(emission.toLowerCase())
        );
        return bikes;
    };

    // Search bikes by price range
    const searchByPriceRange = (minPrice, maxPrice) => {
        const bikes = allProducts.filter(product => 
            product.price >= minPrice && product.price <= maxPrice
        );
        return bikes;
    };

    // Format bike list for display
    const formatBikeList = (bikes) => {
        if (bikes.length === 0) {
            return "Sorry, I couldn't find any bikes matching your criteria. 😔";
        }

        let response = `I found ${bikes.length} bike${bikes.length > 1 ? 's' : ''} for you! 🏍️\n\n`;
        
        bikes.slice(0, 5).forEach((bike, index) => {
            response += `${index + 1}. ${bike.title}\n`;
            response += `   💰 Price: ৳${bike.price?.toLocaleString()}\n`;
            response += `   ⚙️ Engine: ${bike.displacement || bike.emission || 'N/A'}\n`;
            if (bike.max_power) response += `   🔥 Power: ${bike.max_power}\n`;
            response += `\n`;
        });

        if (bikes.length > 5) {
            response += `...and ${bikes.length - 5} more bikes! Visit our Products page to see all.`;
        }

        return response;
    };

    // Enhanced bot response with backend data
    const getBotResponse = (userMessage) => {
        const message = userMessage.toLowerCase();

        // Brand specific searches
        const brands = ['yamaha', 'honda', 'suzuki', 'bajaj', 'ktm', 'kawasaki', 'hero', 'tvs', 'royal enfield', 'apache'];
        for (const brand of brands) {
            if (message.includes(brand)) {
                const bikes = searchByBrand(brand);
                return formatBikeList(bikes) + `\n\nWould you like to know more about any specific model?`;
            }
        }

        // List all brands
        if (message.includes('brands') || message.includes('which brand') || message.includes('available brand')) {
            const uniqueBrands = getUniqueBrands();
            if (uniqueBrands.length > 0) {
                return `We have bikes from these brands:\n${uniqueBrands.map((b, i) => `${i + 1}. ${b}`).join('\n')}\n\nWhich brand would you like to explore?`;
            }
        }

        // CC/Emission searches
        if (message.match(/\d+cc/) || message.includes('150cc') || message.includes('160cc') || message.includes('200cc')) {
            const ccMatch = message.match(/(\d+)cc/);
            if (ccMatch) {
                const bikes = searchByEmission(ccMatch[0]);
                return formatBikeList(bikes);
            }
        }

        // Price range searches
        if (message.includes('under') || message.includes('below') || message.includes('less than')) {
            const priceMatch = message.match(/\d+/);
            if (priceMatch) {
                const maxPrice = parseInt(priceMatch[0]);
                const bikes = searchByPriceRange(0, maxPrice);
                return formatBikeList(bikes);
            }
        }

        if (message.includes('between') && message.match(/\d+/g)) {
            const prices = message.match(/\d+/g);
            if (prices && prices.length >= 2) {
                const minPrice = parseInt(prices[0]);
                const maxPrice = parseInt(prices[1]);
                const bikes = searchByPriceRange(minPrice, maxPrice);
                return formatBikeList(bikes);
            }
        }

        // Show all bikes
        if (message.includes('show all') || message.includes('all bike') || message.includes('available bike')) {
            return formatBikeList(allProducts);
        }

        // Search by model name
        if (message.includes('find') || message.includes('search') || message.includes('looking for')) {
            const searchTerm = message.replace(/find|search|looking for|bike|show me/g, '').trim();
            if (searchTerm.length > 2) {
                const bikes = allProducts.filter(product => 
                    product.title?.toLowerCase().includes(searchTerm)
                );
                if (bikes.length > 0) {
                    return formatBikeList(bikes);
                }
            }
        }

        // Price inquiries
        if (message.includes('price') || message.includes('cost') || message.includes('how much')) {
            if (allProducts.length > 0) {
                const prices = allProducts.map(p => p.price).filter(Boolean);
                const minPrice = Math.min(...prices);
                const maxPrice = Math.max(...prices);
                return `Our bikes range from ৳${minPrice.toLocaleString()} to ৳${maxPrice.toLocaleString()}.\n\nYou can search by:\n• Brand (e.g., "Yamaha bikes")\n• CC (e.g., "150cc bikes")\n• Price range (e.g., "bikes under 200000")\n\nWhat are you looking for?`;
            }
        }

        // Bike inquiries
        if (message.includes('bike') || message.includes('motorcycle') || message.includes('product')) {
            return `We have ${allProducts.length} bikes available! You can:\n\n🏍️ Browse by brand: "Show me Yamaha bikes"\n⚙️ Search by CC: "150cc bikes"\n💰 Filter by price: "Bikes under 300000"\n🔍 Find specific model: "Find Pulsar NS"\n\nWhat would you like to explore?`;
        }

        // Availability
        if (message.includes('available') || message.includes('stock')) {
            return "Most of our bikes are currently in stock! You can ask me about specific brands or models to check availability. Which bike are you interested in?";
        }

        // Delivery/Shipping
        if (message.includes('delivery') || message.includes('shipping') || message.includes('ship')) {
            return "We offer home delivery across Bangladesh! Delivery typically takes 3-7 business days depending on your location. Shipping costs vary by area. Would you like more details about delivery to your area?";
        }

        // Payment
        if (message.includes('payment') || message.includes('pay') || message.includes('emi') || message.includes('installment')) {
            return "We accept multiple payment methods including Cash, Bank Transfer, and EMI options! EMI is available for 6, 12, and 18 months with select banks. Would you like to know more about our payment options?";
        }

        // Test ride
        if (message.includes('test') || message.includes('ride') || message.includes('demo')) {
            return "Yes! We offer test rides at our showroom. To schedule a test ride, please visit our showroom or call us at +880 1234-567890. Which bike would you like to test?";
        }

        // Contact
        if (message.includes('contact') || message.includes('phone') || message.includes('email') || message.includes('address')) {
            return "You can reach us at:\n📧 Email: contact@bikezone.com\n📞 Phone: +880 1234-567890\n📍 Address: 123 Bike Street, Dhaka, Bangladesh\n\nOur team is available 9 AM - 9 PM, 7 days a week!";
        }

        // Warranty
        if (message.includes('warrant') || message.includes('guarantee')) {
            return "All our bikes come with manufacturer warranty ranging from 1-3 years depending on the brand and model. We also offer extended warranty options. Would you like to know about warranty for a specific bike?";
        }

        // Service/Maintenance
        if (message.includes('service') || message.includes('maintenance') || message.includes('repair')) {
            return "We have a fully equipped service center for all maintenance and repairs! Free service is included for the first year. Book your service appointment by calling +880 1234-567890 or visit our service center.";
        }

        // Financing
        if (message.includes('loan') || message.includes('finance') || message.includes('credit')) {
            return "We partner with major banks to offer easy bike financing! Get up to 80% loan amount with flexible EMI options. Required documents: NID, Income proof, and Bank statement. Interested in applying?";
        }

        // Greeting
        if (message.includes('hi') || message.includes('hello') || message.includes('hey')) {
            return "Hello! 😊 Welcome to BikeZone. I'm here to help you find your dream bike! What would you like to know?";
        }

        // Thanks
        if (message.includes('thank') || message.includes('thanks')) {
            return "You're welcome! 😊 Feel free to ask if you have any other questions. Happy to help!";
        }

        // Bye
        if (message.includes('bye') || message.includes('goodbye')) {
            return "Goodbye! 👋 Thank you for visiting BikeZone. Feel free to return anytime. Have a great day!";
        }

        // Default response
        return `I'm here to help! 😊 You can ask me:\n\n🏍️ "Show me Yamaha bikes"\n⚙️ "150cc bikes"\n💰 "Bikes under 300000"\n🔍 "Find Pulsar"\n📋 "What brands do you have?"\n📞 "Contact information"\n\nWhat would you like to know?`;
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;

        const userMsg = {
            type: 'user',
            text: inputMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMsg]);
        const messageToProcess = inputMessage;
        setInputMessage('');
        setIsTyping(true);

        // Simulate bot typing delay
        setTimeout(() => {
            const botResponse = getBotResponse(messageToProcess);
            const botMsg = {
                type: 'bot',
                text: botResponse,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, botMsg]);
            setIsTyping(false);
        }, 1000 + Math.random() * 1000);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const quickActions = [
        { icon: '🏍️', text: 'View All Bikes', action: 'Show me all bikes' },
        { icon: '🏢', text: 'Brands', action: 'What brands do you have?' },
        { icon: '💰', text: 'Budget Bikes', action: 'Bikes under 200000' },
        { icon: '📞', text: 'Contact', action: 'How can I contact you?' }
    ];

    const handleQuickAction = (action) => {
        setInputMessage(action);
        setTimeout(() => handleSendMessage(), 100);
    };

    return (
        <>
            {/* Chat Button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    style={{
                        position: 'fixed',
                        bottom: '30px',
                        right: '30px',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        transition: 'all 0.3s ease',
                        animation: 'pulse 2s infinite',
                        fontSize: '28px'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 30px rgba(102, 126, 234, 0.6)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)';
                    }}
                >
                    💬
                    <style>{`
                        @keyframes pulse {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.05); }
                        }
                    `}</style>
                </button>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div style={{
                    position: 'fixed',
                    bottom: '30px',
                    right: '30px',
                    width: '400px',
                    height: '650px',
                    maxHeight: '80vh',
                    background: 'white',
                    borderRadius: '20px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 1000,
                    overflow: 'hidden',
                    animation: 'slideUp 0.3s ease'
                }}>
                    <style>{`
                        @keyframes slideUp {
                            from {
                                transform: translateY(20px);
                                opacity: 0;
                            }
                            to {
                                transform: translateY(0);
                                opacity: 1;
                            }
                        }
                    `}</style>

                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '20px',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px'
                            }}>
                                🤖
                            </div>
                            <div>
                                <div style={{ fontWeight: '700', fontSize: '16px' }}>BikeZone Assistant</div>
                                <div style={{ fontSize: '12px', opacity: '0.9', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        background: '#4ade80',
                                        animation: 'pulse-dot 2s infinite'
                                    }}></span>
                                    Online • Replies instantly
                                </div>
                                <style>{`
                                    @keyframes pulse-dot {
                                        0%, 100% { opacity: 1; }
                                        50% { opacity: 0.5; }
                                    }
                                `}</style>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            style={{
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                fontSize: '20px',
                                color: 'white'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                        >
                            ✕
                        </button>
                    </div>

                    {/* Quick Actions */}
                    {messages.length <= 1 && (
                        <div style={{
                            padding: '15px',
                            background: '#f8f9fa',
                            borderBottom: '1px solid #e9ecef'
                        }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px'
                            }}>
                                {quickActions.map((action, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleQuickAction(action.action)}
                                        style={{
                                            padding: '10px',
                                            background: 'white',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#667eea';
                                            e.currentTarget.style.color = 'white';
                                            e.currentTarget.style.borderColor = '#667eea';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'white';
                                            e.currentTarget.style.color = 'inherit';
                                            e.currentTarget.style.borderColor = '#dee2e6';
                                        }}
                                    >
                                        <span>{action.icon}</span>
                                        <span>{action.text}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Messages */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '20px',
                        background: '#f8f9fa'
                    }}>
                        {messages.map((message, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                                    marginBottom: '15px',
                                    animation: 'fadeIn 0.3s ease'
                                }}
                            >
                                <div style={{
                                    maxWidth: '85%',
                                    padding: '12px 16px',
                                    borderRadius: message.type === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                    background: message.type === 'user' 
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : 'white',
                                    color: message.type === 'user' ? 'white' : '#2c3e50',
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    whiteSpace: 'pre-line'
                                }}>
                                    {message.text}
                                </div>
                            </div>
                        ))}

                        {isTyping && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                marginBottom: '15px'
                            }}>
                                <div style={{
                                    padding: '12px 16px',
                                    borderRadius: '18px 18px 18px 4px',
                                    background: 'white',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                }}>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <span style={{ animation: 'bounce 1.4s infinite', fontSize: '12px' }}>●</span>
                                        <span style={{ animation: 'bounce 1.4s infinite 0.2s', fontSize: '12px' }}>●</span>
                                        <span style={{ animation: 'bounce 1.4s infinite 0.4s', fontSize: '12px' }}>●</span>
                                    </div>
                                    <style>{`
                                        @keyframes bounce {
                                            0%, 60%, 100% { transform: translateY(0); }
                                            30% { transform: translateY(-10px); }
                                        }
                                        @keyframes fadeIn {
                                            from { opacity: 0; transform: translateY(10px); }
                                            to { opacity: 1; transform: translateY(0); }
                                        }
                                    `}</style>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div style={{
                        padding: '15px',
                        background: 'white',
                        borderTop: '1px solid #e9ecef'
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center'
                        }}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Ask about bikes..."
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    borderRadius: '25px',
                                    border: '1px solid #dee2e6',
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'all 0.3s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                            />
                            <button
                                onClick={handleSendMessage}
                                disabled={!inputMessage.trim()}
                                style={{
                                    width: '45px',
                                    height: '45px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: inputMessage.trim() 
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : '#e9ecef',
                                    cursor: inputMessage.trim() ? 'pointer' : 'not-allowed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s',
                                    opacity: inputMessage.trim() ? 1 : 0.5,
                                    fontSize: '20px'
                                }}
                                onMouseEnter={(e) => {
                                    if (inputMessage.trim()) {
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                            >
                                ➤
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatBot;